// The browser's side of the proxy.
//
// NOT a mock, and not a SWAP POINT — this talks to a real endpoint that talks
// to a real provider. What it does own is the translation of every failure
// into a typed reason the UI can say out loud, because "no shops near you" and
// "we couldn't ask" are different facts and the user deserves to know which
// one they are looking at.
//
// Nothing here falls back to stored or sample stores. If the lookup fails, the
// answer is that it failed.

const BASE = process.env.NOTDELIVERY_API_BASE || '';

// `unconfigured` and `provider` are ours to fix, not the user's — the UI
// offers no retry for them, because retrying changes nothing.
const RETRYABLE = new Set(['network', 'timeout', 'rate-limited']);

export class StoreLookupError extends Error {
  constructor(reason, message) {
    super(message || reason);
    this.name = 'StoreLookupError';
    this.reason = reason;
    this.retryable = RETRYABLE.has(reason);
  }
}

export function isLookupCancellation(error) {
  return error?.name === 'AbortError' || error?.reason === 'cancelled';
}

async function post(endpoint, body, signal) {
  let response;
  try {
    response = await fetch(`${BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    // The proxy being down looks exactly like the network being down from
    // here, and both are worth a retry, so they share a reason.
    throw new StoreLookupError('network', 'Could not reach the store service.');
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const reason = payload?.error?.reason ?? 'provider';
    throw new StoreLookupError(reason, payload?.error?.message);
  }
  return payload ?? {};
}

// Returns every nearby grocery branch the provider knows about, unranked and
// unfiltered beyond what the mapping drops. Ranking is the caller's job — see
// lib/rankStores.js — so this stays a transport function.
export async function searchNearbyStores({ lat, lon, radiusMeters, signal } = {}) {
  const payload = await post('/api/places/nearby', { lat, lon, radiusMeters }, signal);
  return payload.stores ?? [];
}

// `null` means the address matched nothing — a real answer, not an error, and
// the form says "we couldn't find that" rather than "something went wrong".
export async function geocodePlace({ query, countryCode, signal } = {}) {
  const payload = await post('/api/places/geocode', { query, countryCode }, signal);
  return payload.place ?? null;
}
